import classNames from 'classnames'
import React, { useMemo } from 'react'

import ButtonIcon, { shouldRenderButtonIcon } from './button-icon'
import type { ButtonIconProps, ButtonProps } from './types'
import type { FlexContainerProperties } from '../../theme-provider/types'
import isReact from '../../utils/is-react'
import { EMPTY_OBJECT } from '../../utils/object'
import Flex from '../_primitives/flex/flex'
import type { TextProps } from '../_primitives/text'
import Text from '../_primitives/text'
import type { SizeProps } from '../_primitives/types'

const ButtonIconMemo = React.memo(ButtonIcon)

function IconTopOfTextLayout(props: {
  role: ButtonProps['role']
  buttonLabel: string
  removeText: boolean
  alignItems: FlexContainerProperties['alignItems']
  icon: ButtonIconProps
  lhCrop?: ButtonProps['lhCrop']
  textWrap: ButtonProps['textWrap']
  iconSize?: SizeProps
  stringNodes: React.ReactNode[]
  children: React.ReactNode
  textProps?: TextProps
  isLoading?: boolean
}): JSX.Element {
  const {
    role,
    buttonLabel,
    removeText,
    icon,
    lhCrop,
    textWrap,
    iconSize,
    stringNodes = [],
    children,
    textProps = EMPTY_OBJECT as TextProps,
    isLoading
  } = props

  const { sx, ...rest } = textProps

  const sxStyles = useMemo(
    () => ({
      '&.is-hidden': {
        opacity: 0
      },
      ...sx
    }),
    [sx]
  )

  return (
    <>
      {shouldRenderButtonIcon(icon) ? (
        <ButtonIconMemo {...{ buttonLabel, icon, iconSize, isLoading }} />
      ) : isReact.element(icon) ? (
        <Flex opacity={isLoading ? 0 : 1}>{icon}</Flex>
      ) : null}
      {buttonLabel && !removeText && stringNodes.length > 0 ? (
        <Text
          className={classNames({
            'is-hidden': isLoading
          })}
          lhCrop={lhCrop}
          ellipsis={!textWrap}
          fontSize={'0.75em'}
          content={buttonLabel}
          role={role}
          sx={sxStyles}
          {...rest}
        >
          {stringNodes}
        </Text>
      ) : (
        children
      )}
    </>
  )
}

export default IconTopOfTextLayout
